import type EditorJS from '@editorjs/editorjs';

import { toaster } from '#components/ui/toaster';

export function normalizeLinkUrl(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;

  if (t.startsWith('/') || t.startsWith('#')) return t;

  if (/^mailto:/i.test(t)) return t;

  try {
    new URL(t);
    return t;
  } catch {
    try {
      return new URL(`https://${t}`).href;
    } catch {
      return null;
    }
  }
}

function escapeHtmlText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeHrefAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

/**
 * Applies a link at the current selection (or inserts at caret when collapsed).
 * When `displayLabel` is set, it becomes the anchor text; otherwise the selected
 * text is kept (non-collapsed) or the URL is shown (collapsed).
 */
export function applyLinkUrl(
  editor: EditorJS,
  raw: string,
  displayLabel?: string,
): void {
  const url = normalizeLinkUrl(raw);
  if (!url) {
    toaster.create({
      title: 'Invalid URL',
      type: 'warning',
    });
    return;
  }

  void editor.focus();

  const sel = window.getSelection();
  const trimmedLabel = displayLabel?.trim();

  if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
    if (editor.selection.findParentTag('A')) {
      toaster.create({
        title: 'Remove the existing link first.',
        type: 'info',
      });
      return;
    }

    const safeHref = escapeHrefAttr(url);
    if (trimmedLabel) {
      const labelHtml = escapeHtmlText(trimmedLabel);
      document.execCommand(
        'insertHTML',
        false,
        `<a href="${safeHref}">${labelHtml}</a>`,
      );
    } else {
      document.execCommand('createLink', false, url);
    }

    return;
  }

  const safeHref = escapeHrefAttr(url);
  const visible = trimmedLabel
    ? escapeHtmlText(trimmedLabel)
    : escapeHtmlText(url);
  document.execCommand(
    'insertHTML',
    false,
    `<a href="${safeHref}">${visible}</a>`,
  );
}
